import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId } from "@/hooks/useLenis";
import { PROFILE, IMAGES } from "@/data/content";

const LINKS = [
  { label: "Story", id: "story" },
  { label: "Videos", id: "videos" },
  { label: "Press", id: "press" },
  { label: "Partners", id: "partners" },
  { label: "Say Hi", id: "contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 120, damping: 16 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "backdrop-blur-xl bg-obsidian/80 border-b-2 border-blue/30" : "bg-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-5 md:px-10 h-[72px] md:h-20 flex items-center justify-between">
        <motion.button
          data-testid="nav-logo"
          onClick={() => go("top")}
          whileHover={{ rotate: -8, scale: 1.08 }}
          whileTap={{ scale: 0.92, rotate: 8 }}
          transition={{ type: "spring", stiffness: 300, damping: 12 }}
          className="relative shrink-0"
          aria-label="Justin Leusner home"
        >
          <img
            src={IMAGES.face}
            alt="Justin Leusner"
            className="h-12 w-12 md:h-14 md:w-14 rounded-full object-cover object-center bg-white/5"
          />
        </motion.button>

        <div className="hidden md:flex items-center gap-2">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="font-outfit text-sm uppercase tracking-wide text-white px-4 py-2 rounded-full hover:bg-white/10 hover:text-yellow transition-colors duration-200"
            >
              {l.label}
            </button>
          ))}
          <motion.button
            data-testid="nav-cta"
            onClick={() => go("contact")}
            whileHover={{ scale: 1.05, rotate: -2 }}
            whileTap={{ scale: 0.95 }}
            className="ml-2 font-outfit font-extrabold text-sm uppercase tracking-wide bg-blue text-obsidian px-6 py-3 rounded-full hover:bg-yellow transition-colors duration-200"
          >
            Work With Me
          </motion.button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-white bg-white/10 rounded-full p-2"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-obsidian border-b-2 border-blue/30"
          >
            <div className="px-5 py-6 flex flex-col gap-3">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="text-left font-anton text-2xl uppercase text-white hover:text-yellow transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-cta"
                onClick={() => go("contact")}
                className="mt-2 font-outfit font-extrabold uppercase bg-blue text-obsidian px-5 py-3 rounded-full"
              >
                Work With Me
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
